import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight } from "lucide-react";
import ImageGallery from "@/components/ImageGallery";
import { SiteImage, PLACEHOLDER_COLOR, FADE_DURATION, FADE_EASING } from "@/components/SiteImage";
import { FadeIn } from "@/hooks/useFadeIn";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ken Burns zoom-out configuration - Desktop
const HERO_ZOOM_START_DESKTOP = 1.05; // Start slightly zoomed in
const HERO_ZOOM_END = 1.00;           // End at normal scale
const HERO_ZOOM_DURATION_DESKTOP = 6000; // 6 seconds for subtle effect

// Ken Burns zoom-out configuration - Mobile (more noticeable)
const HERO_ZOOM_START_MOBILE = 1.10;  // Start more zoomed in on mobile
const HERO_ZOOM_DURATION_MOBILE = 9000; // 9 seconds for mobile

// Mobile scroll-reactive effect configuration
const MOBILE_SCROLL_MAX_TRANSLATE = 10;   // Maximum 10px translateY movement
const MOBILE_SCROLL_RANGE = 250;          // Effect applies within first 250px of scroll
const MOBILE_SCROLL_MIN_OPACITY = 0.98;   // Subtle dim effect (1.00 -> 0.98)

/**
 * ResponsiveHeroImage - Hero with desktop/mobile image support
 * 
 * Architecture: Two nested layers for independent transforms
 * - Outer layer (Ken Burns): handles scale animation (CSS transition)
 * - Inner layer (Scroll): handles translateY + opacity on mobile (JS-driven)
 * 
 * Features:
 * - Fade-in on load (400ms)
 * - Ken Burns zoom-out (Desktop: 1.05→1.00/6s, Mobile: 1.10→1.00/9s)
 * - Mobile: Scroll-reactive depth effect (max 10px translateY + subtle dim)
 * - Both effects run simultaneously without conflict
 * - Respects prefers-reduced-motion
 */
const ResponsiveHeroImage = ({ desktopSrc, mobileSrc, alt = "" }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0); // 0 to 1
  const containerRef = useRef(null);
  const rafRef = useRef(null);
  const prefersReducedMotion = useRef(
    typeof window !== "undefined" && 
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  // Detect mobile viewport on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Mobile scroll-reactive effect (separate from Ken Burns)
  useEffect(() => {
    // Only apply on mobile and when reduced motion is not preferred
    if (!isMobile || prefersReducedMotion.current) {
      setScrollProgress(0);
      return;
    }

    const handleScroll = () => {
      // Cancel any pending animation frame
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }

      rafRef.current = requestAnimationFrame(() => {
        if (!containerRef.current) return;

        const rect = containerRef.current.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        // Only apply effect when hero is visible
        if (rect.bottom < 0 || rect.top > viewportHeight) {
          setScrollProgress(0);
          return;
        }

        // Linear progress from 0 to 1 over the first MOBILE_SCROLL_RANGE pixels
        const scrollY = window.scrollY;
        const progress = Math.min(scrollY / MOBILE_SCROLL_RANGE, 1);
        
        setScrollProgress(progress);
      });
    };

    // Initial call
    handleScroll();

    window.addEventListener("scroll", handleScroll, { passive: true });
    
    return () => {
      window.removeEventListener("scroll", handleScroll);
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
      }
    };
  }, [isMobile]);

  // Preload the appropriate image based on viewport
  useEffect(() => {
    setIsLoaded(false);
    
    if (!desktopSrc) return;

    const mobile = window.innerWidth < 768;
    const srcToPreload = (mobile && mobileSrc) ? mobileSrc : desktopSrc;

    const img = new Image();
    img.onload = () => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setIsLoaded(true);
        });
      });
    };
    img.src = srcToPreload;

    return () => {
      img.onload = null;
    };
  }, [desktopSrc, mobileSrc]);

  // Ken Burns layer styles (OUTER layer - handles scale + fade)
  const getKenBurnsStyle = () => {
    const zoomStart = isMobile ? HERO_ZOOM_START_MOBILE : HERO_ZOOM_START_DESKTOP;
    const zoomDuration = isMobile ? HERO_ZOOM_DURATION_MOBILE : HERO_ZOOM_DURATION_DESKTOP;

    if (prefersReducedMotion.current) {
      return {
        opacity: isLoaded ? 1 : 0,
        transform: `scale(${HERO_ZOOM_END})`,
        transition: "none",
      };
    }

    return {
      opacity: isLoaded ? 1 : 0,
      transform: `scale(${isLoaded ? HERO_ZOOM_END : zoomStart})`,
      transition: isLoaded 
        ? `opacity ${FADE_DURATION}ms ${FADE_EASING}, transform ${zoomDuration}ms ease-out`
        : "none",
    };
  };

  // Scroll-reactive layer styles (INNER layer - handles translateY + opacity, mobile only)
  const getScrollStyle = () => {
    if (prefersReducedMotion.current || !isMobile) {
      return {}; // No scroll effect on desktop or reduced motion
    }

    // Linear interpolation based on scroll progress (0 to 1)
    const translateY = scrollProgress * MOBILE_SCROLL_MAX_TRANSLATE;
    const opacity = 1 - (scrollProgress * (1 - MOBILE_SCROLL_MIN_OPACITY));

    return {
      transform: `translateY(-${translateY}px)`,
      opacity: opacity,
      // No transition - instant response to scroll for smooth feel
    };
  };

  if (!desktopSrc) {
    return <div className="absolute inset-0" style={{ backgroundColor: PLACEHOLDER_COLOR }} />;
  }

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden">
      {/* Placeholder background */}
      <div 
        className="absolute inset-0"
        style={{ backgroundColor: PLACEHOLDER_COLOR }}
      />
      
      {/* OUTER LAYER: Ken Burns scale animation */}
      <div 
        className="absolute inset-0"
        style={getKenBurnsStyle()}
      >
        {/* INNER LAYER: Scroll-reactive translateY (mobile only) */}
        <div 
          className="absolute inset-0"
          style={getScrollStyle()}
        >
          {/* Image element */}
          <picture>
            {mobileSrc && (
              <source 
                media="(max-width: 767px)" 
                srcSet={mobileSrc}
              />
            )}
            <img
              src={desktopSrc}
              alt={alt}
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-image"
            />
          </picture>
        </div>
      </div>
    </div>
  );
};

const HomePage = () => {
  const [gallery, setGallery] = useState(null);
  const [heroImages, setHeroImages] = useState({ desktop: null, mobile: null });
  const [loading, setLoading] = useState(true);
  const [siteConfig, setSiteConfig] = useState({ phone: "+45 12 34 56 78", email: "", cvr: "" });
  const [serviceImages, setServiceImages] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        
        // Fetch site config (includes hero images)
        const configRes = await axios.get(`${API}/config`);
        setSiteConfig(configRes.data);
        
        // Set hero images from config only (single source of truth)
        setHeroImages({
          desktop: configRes.data.heroImageDesktopUrl || null,
          mobile: configRes.data.heroImageMobileUrl || null
        });
        
        // Fetch service images from the same source as Services page
        const servicesRes = await axios.get(`${API}/galleries/ydelser`);
        const sortedImages = (servicesRes.data?.images || [])
          .sort((a, b) => a.order - b.order)
          .map(img => img.url);
        setServiceImages(sortedImages);
        
        // Fetch project showcase gallery (small cards under hero)
        const res = await axios.get(`${API}/galleries/forside-projekter`);
        setGallery(res.data);
      } catch (err) {
        console.error("Error fetching gallery:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  // Format phone for tel: link (remove spaces)
  const phoneLink = `tel:${siteConfig.phone.replace(/\s+/g, '')}`;

  // Service cards mapped to admin image indices (same as Services page)
  // Index 0: Flisebelægning, Index 1: Natursten, Index 2: Rullegræs, Index 3: Hegn
  const services = [
    {
      title: "Flisebelægning",
      description: "Terrasser, gangarealer og indkørsler med solid bundopbygning.",
      imageIndex: 0,
      link: "/ydelser#flisebelaegning",
    },
    {
      title: "Rullegræs",
      description: "Færdige græsplæner med professionel jordklargøring.",
      imageIndex: 2,
      link: "/ydelser#rullegraes",
    },
    {
      title: "Hegn",
      description: "Solide hegn i træ med holdbare fundamenter.",
      imageIndex: 3,
      link: "/ydelser#hegn",
    },
  ];

  // Get service image by index with fallback
  const getServiceImage = (index) => {
    return serviceImages[index] || null;
  };

  return (
    <div data-testid="home-page">
      <SEO 
        title="Birk Anlæg | Anlægsgartner i København"
        description="Birk Anlæg udfører anlægsarbejde i København og omegn med fokus på kvalitet, pålidelighed og solidt håndværk. Flisebelægning, rullegræs, hegn og meget mere. Over 20 års erfaring."
        canonical="/"
      />
      {/* Hero Section - Responsive images with consistent fade (same as SiteImage) */}
      <section 
        className="relative h-[50vh] md:h-[55vh] overflow-hidden"
        data-testid="hero-section"
      >
        <ResponsiveHeroImage 
          desktopSrc={heroImages.desktop}
          mobileSrc={heroImages.mobile}
          alt="Birk Anlæg hero billede"
        />
      </section>

      {/* Hero Content - Below image on white background */}
      <section className="bg-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            data-testid="hero-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A3C34] mb-6 leading-tight"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Anlægsgartner med fokus på håndværket
          </h1>
          <p className="text-lg md:text-xl text-gray-600 mb-10 max-w-2xl mx-auto leading-relaxed">
            Birk Anlæg udfører anlægsarbejde i København og omegn med fokus på 
            kvalitet, pålidelighed og solidt håndværk.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="hero-cta-quote"
              data-cta="primary"
              data-cta-variant="green"
              className="bg-[#1A3C34] text-white rounded-full px-10 py-4 text-lg font-medium inline-flex items-center justify-center gap-2"
            >
              Få et tilbud
              <ArrowRight size={20} />
            </Link>
            <Link
              to="/kontakt"
              data-testid="hero-cta-contact"
              data-cta="secondary"
              data-cta-color="green"
              className="bg-transparent text-[#1A3C34] border-2 border-[#1A3C34] rounded-full px-10 py-4 text-lg font-medium inline-flex items-center justify-center"
            >
              Kontakt os
            </Link>
          </div>
        </div>
      </section>

      {/* Services Preview */}
      <FadeIn>
        <section data-testid="services-preview" className="py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2
                className="text-4xl md:text-5xl font-semibold text-[#1A3C34] mb-4"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Vores ydelser
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Vi tilbyder et bredt udvalg af anlægsopgaver til boligforeninger, 
                virksomheder og private.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {services.map((service, index) => (
                <Link
                  key={index}
                  to={service.link}
                  data-testid={`service-card-${index}`}
                  className="group service-card bg-white overflow-hidden"
                >
                  <SiteImage
                    src={getServiceImage(service.imageIndex)}
                    alt={service.title}
                    aspectRatio="4/3"
                    className="transition-transform duration-500 group-hover:scale-110"
                    loading="lazy"
                  />
                  <div className="p-6">
                    <h3
                      className="text-xl font-semibold text-[#1A3C34] mb-2 group-hover:text-[#142F29] transition-colors"
                      style={{ fontFamily: "Manrope, sans-serif" }}
                    >
                      {service.title}
                    </h3>
                    <p className="text-gray-600 mb-4">{service.description}</p>
                    <span className="text-[#1A3C34] font-medium flex items-center gap-2 group-hover:gap-4 transition-all">
                      Læs mere <ArrowRight size={18} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            <div className="text-center mt-12">
              <Link
                to="/ydelser"
                data-testid="all-services-link"
                data-cta="primary"
                data-cta-variant="green"
                className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium text-lg"
              >
                Se alle ydelser <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>
      </FadeIn>

      {/* Project Showcase */}
      <FadeIn>
        <section data-testid="projects-section" className="bg-[#F9FAFB] py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-16">
              <h2
                className="text-4xl md:text-5xl font-semibold text-[#1A3C34] mb-4"
                style={{ fontFamily: "Manrope, sans-serif" }}
              >
                Udvalgte projekter
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Se eksempler på vores seneste arbejde i København og omegn.
              </p>
            </div>

            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="aspect-[4/3] bg-gray-200 animate-pulse rounded-lg" />
                ))}
              </div>
            ) : (
              <ImageGallery
                images={gallery?.images || []}
                columns={3}
                showLightbox={true}
              />
            )}

            <div className="text-center mt-12">
              <Link
                to="/galleri"
                data-testid="all-projects-link"
                data-cta="primary"
                data-cta-variant="green"
                className="inline-flex items-center gap-2 bg-[#1A3C34] text-white rounded-full px-8 py-4 font-medium"
              >
                Se alle projekter <ArrowRight size={20} />
              </Link>
            </div>
          </div>
        </section>
      </FadeIn>

      {/* Testimonials Section */}
      <section data-testid="testimonials-section" className="bg-white pt-28 md:pt-32 pb-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2
            className="text-3xl md:text-4xl font-semibold text-[#1A3C34] text-center mb-16"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Udvalgte kundeudtalelser
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-10 lg:gap-12">
            {/* Testimonial 1 */}
            <div className="flex flex-col max-w-sm mx-auto md:max-w-none" data-testid="testimonial-1">
              <blockquote className="text-gray-600 text-lg leading-relaxed mb-2">
                Vi fik god rådgivning omkring materialerne, og hele processen føltes tryg fra start til slut. Terrassen er blevet præcis, som vi havde håbet.
              </blockquote>
              <p className="text-[#1A3C34] font-semibold mt-auto">
                Anne, Brønshøj
              </p>
            </div>
            
            {/* Testimonial 2 */}
            <div className="flex flex-col max-w-sm mx-auto md:max-w-none" data-testid="testimonial-2">
              <blockquote className="text-gray-600 text-lg leading-relaxed mb-2">
                Der var god fleksibilitet undervejs, og justeringer blev håndteret uden problemer. Resultatet står skarpt og lever op til det, vi havde aftalt.
              </blockquote>
              <p className="text-[#1A3C34] font-semibold mt-auto">
                Søren, Gentofte
              </p>
            </div>
            
            {/* Testimonial 3 */}
            <div className="flex flex-col max-w-sm mx-auto md:max-w-none" data-testid="testimonial-3">
              <blockquote className="text-gray-600 text-lg leading-relaxed mb-2">
                Vi oplevede stor tillid i samarbejdet, og løsningen blev udviklet i tæt dialog undervejs. Gårdmiljøet fungerer godt i hverdagen og er blevet taget positivt imod.
              </blockquote>
              <p className="text-[#1A3C34] font-semibold mt-auto">
                Bestyrelsen, A/B Asta Nielsen, Valby
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - NO FadeIn wrapper to prevent any opacity issues */}
      <section data-testid="cta-section" className="bg-[#1A3C34] py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-semibold text-white mb-6"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Klar til at komme i gang?
          </h2>
          <p className="text-xl text-gray-300 mb-10 max-w-2xl mx-auto">
            Kontakt os i dag for et uforpligtende tilbud. Vi vender tilbage 
            inden for 24 timer.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/kontakt"
              data-testid="cta-quote-btn"
              data-cta="primary"
              data-cta-variant="white"
              className="bg-white text-[#1A3C34] rounded-full px-10 py-4 text-lg font-medium inline-flex items-center justify-center gap-2"
            >
              Få et tilbud
              <ArrowRight size={20} />
            </Link>
            <a
              href={phoneLink}
              data-testid="cta-phone-btn"
              data-cta="secondary"
              data-cta-color="white"
              className="bg-transparent text-white border-2 border-white rounded-full px-10 py-4 text-lg font-medium inline-flex items-center justify-center"
            >
              {siteConfig.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
